import type * as Sentry from '@sentry/vue';

/**
 * Filters sensitive data from Sentry events before sending to prevent
 * leaking authentication tokens and authorization headers.
 *
 * @param event - Sentry error or transaction event
 * @returns Filtered event safe for transmission
 */
export const filterSentryData = <T extends Sentry.Event>(event: T): T => {
	if (event.request?.headers?.Authorization) {
		event.request.headers.Authorization = '[Filtered]';
	}

	if (event.request?.url) {
		try {
			const url = new URL(event.request.url);
			url.searchParams.delete('token');
			event.request.url = url.toString();
		} catch (error) {
			console.warn('Failed to parse URL for Sentry filtering', error);
		}
	}

	return event;
};

/**
 * Parses and validates a sample rate value from environment variable.
 * Ensures the value is a valid number between 0 and 1.
 *
 * @param value - Environment variable value
 * @param fallback - Default value if parsing fails (default: 0)
 * @returns Valid sample rate
 */
export const parseSampleRate = (
	value: string | undefined,
	fallback: number = 0
): number => {
	const parsed = parseFloat(value || String(fallback));
	return Number.isNaN(parsed) ? fallback : parsed;
};
